import { isIosNativeNavigation } from "feat/nativeNavigation/platform";
import { presentedNativeRoot } from "feat/nativeNavigation/root-state";
import type { ITokenResponse } from "feat/oauth/interfaces";
import type { RouteNode } from "routing/route";

/** An auth-flow link being resolved. While one is pending, the pre-auth root stays up. */
export interface PendingAuthLink {
  /** The auth-flow route this link resolved to. */
  kind: string;
  /** The full path being resolved, query string and hash intact. */
  path: string;
  /** The account this link is for, once something can tell. */
  forAccount?: string;
  /** The account already signed in when the link arrived. */
  signedInAccount?: string;
  /** Tokens obtained while resolving the link, not yet committed to the session. */
  tokens?: ITokenResponse;
  /** The invite token, present when this mismatch came from an invite link. */
  inviteToken?: string;
  /** The org the invite token above belongs to. */
  inviteOrg?: string;
  /** The route this mismatch came from. `kind` is the mismatch screen's own route. */
  originKind?: string;
}

// Module state, read directly by the native root. Survives the store reset on logout.
let pending: PendingAuthLink | null = null;

// Whether `pending.path` has already been pushed, so a repeat flush does not push it again.
let applied = false;

type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener();
}

/** The auth link currently being resolved, or null when none is pending. */
export function pendingAuthLink(): PendingAuthLink | null {
  return pending;
}

/** Subscribes to changes in the pending auth link. Returns an unsubscribe function. */
export function subscribePendingAuthLink(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Whether a push is needed to land the pending link's path. Always off iOS, and on iOS only
 * when the auth root is already presented, since a root swap carries the path there itself.
 */
function needsPush(): boolean {
  return !isIosNativeNavigation() || presentedNativeRoot() === "auth";
}

/** Pushes the pending link's path once the router can take it, at most once per link. */
function applyPendingAuthLink(): void {
  if (!pending || applied || typeof window.pushPath !== "function") return;
  applied = true;
  if (!needsPush()) return;
  void window.pushPath(pending.path);
}

/** The account fields a resolver fills in once it knows who a link is for. */
export interface PendingAuthLinkAccounts {
  forAccount: string;
  signedInAccount: string;
  tokens?: ITokenResponse;
  inviteToken?: string;
  inviteOrg?: string;
  originKind?: string;
}

/**
 * Starts resolving the auth-flow link at `target`, matched to registry node `route`, with any
 * identity already resolved. Replaces any pending link.
 */
export function setPendingAuthLink(route: RouteNode, target: string, accounts?: PendingAuthLinkAccounts): void {
  pending = { kind: route.path, path: target, ...accounts };
  applied = false;
  notify();
  applyPendingAuthLink();
}

/** Applies the pending auth link now the router is ready. Called by GlobalRouter once it is. */
export function flushPendingAuthLink(): void {
  applyPendingAuthLink();
}

/** Clears the pending auth link. The only way back down to the tabs root. */
export function clearPendingAuthLink(): void {
  if (!pending) return;
  pending = null;
  applied = false;
  notify();
}
